import { inject, Injectable } from '@angular/core';
import { ArticleNumberSchema, ComplexArticleBlueprintDTO, ComplexArticleBlueprintRunMode, ParsedArticleNumberSchema } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-blueprint';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { ComplexArticleBlueprintFactory } from '@admintool/article/generate-article/complex-article-blueprints/services/complex-article-blueprint.factory';
import { ComplexArticleBlueprintDataService } from '@admintool/article/generate-article/complex-article-blueprints/services/complex-article-blueprint.data.service';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { CompositeConfigurationByGivingAreasVO } from '@article/model/vo/composite-article-configuration.vo';
import { ArticleDataService } from '@article/service/article-data.service';
import { Properties } from '@article/util/properties';
import { ArticleProjectGroupDataService } from '@project/components/article/manager/article-project-group-data.service';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { SaveAsCompositeMode, SaveProjectService } from '@project/components/project/project-save/save-project.service';
import { CreateProjectService } from '@project/service/create-project.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { Log } from '@shared/debug/log';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArrayUtils } from '@shared/util/array-utils';
import { ErrorUtils } from '@shared/util/error-utils';
import { FileUtils } from '@shared/util/file-utils';
import { firstValueFrom } from 'rxjs';

export type ComplexArticleBlueprintValidation = { errorsText: string, valid: false } | {errorsText: undefined, valid: true};

@Injectable({providedIn: 'root'})
export class ComplexArticleBlueprintService {
  private readonly alertHelper = inject(AlertHelper);
  private readonly saveProjectService = inject(SaveProjectService);
  private readonly articleProjectGroupDataService = inject(ArticleProjectGroupDataService);
  private readonly createProjectService = inject(CreateProjectService);
  private readonly complexArticleBlueprintRuleFactory = inject(ComplexArticleBlueprintFactory);
  private readonly complexArticleBlueprintDataService = inject(ComplexArticleBlueprintDataService);
  private readonly selectionModelService = inject(SelectionModelService);
  private readonly articleDataService = inject(ArticleDataService);
  private readonly projectModelService = inject(ProjectModelService);
  private readonly localeResourceManager = inject(LocaleResourceManager);

  public async importBlueprint(): Promise<ComplexArticleBlueprintDTO | undefined> {
    let fileContent: string;
    try {
      fileContent = await FileUtils.loadTextFile('.json');
    } catch (error) {
      FileUtils.handleFileLoadingError(error, this.alertHelper);
      return undefined;
    }

    let importedBlueprint: ComplexArticleBlueprintDTO;
    try {
      importedBlueprint = JSON.parse(fileContent);
    } catch (error) {
      Log.error('Could not parse imported blueprint json: ', error);
      await this.alertHelper.showError('global.label.error');
      return undefined;
    }

    const { id: _id, ...blueprintWithoutId } = importedBlueprint;
    const createdBlueprint = await firstValueFrom(this.complexArticleBlueprintDataService.create(blueprintWithoutId));
    await firstValueFrom(this.complexArticleBlueprintDataService.save(createdBlueprint));
    return createdBlueprint;
  }

  public exportBlueprint(blueprint: ComplexArticleBlueprintDTO): void {
    FileUtils.saveTextFile(blueprint.name, JSON.stringify(blueprint, null, 2), '.json');
  }

  /** @returns the articleNos of the generated articles */
  public async runBlueprint(blueprint: ComplexArticleBlueprintDTO, runMode: ComplexArticleBlueprintRunMode): Promise<string[]> {
    if (!this.validateBlueprint(blueprint).valid) return [];
    let articleNumberSchemas = this.parseArticleNumberSchemas(blueprint.articleNumberSchema, blueprint.buildingBlockName);

    if (runMode.versionOverwriteMode == 'only-new-articles') {
      articleNumberSchemas = await this.filterAlreadyExistingArticleNos(articleNumberSchemas);
    }

    const generatedArticleNos: string[] = [];
    for (const articleNumberSchema of articleNumberSchemas) {
      const succeeded = await this.runDefinition(blueprint, articleNumberSchema, runMode);
      if(succeeded) {
        generatedArticleNos.push(articleNumberSchema.articleNo);
      }
    }
    await this.projectModelService.clearCurrentProject();
    return generatedArticleNos;
  }

  private async filterAlreadyExistingArticleNos(articleNumberSchemas: ParsedArticleNumberSchema[]): Promise<ParsedArticleNumberSchema[]> {
    const articleNos = articleNumberSchemas.map(schema => schema.articleNo);
    const alreadyExistingArticleNos = (await firstValueFrom(this.articleDataService.getLatestArticlesByNoObservable(articleNos)))
      .map(article => article.articleNo);

    if (alreadyExistingArticleNos.length > 0) {
      articleNumberSchemas = articleNumberSchemas.filter(schema => !alreadyExistingArticleNos.includes(schema.articleNo));
      const alreadyExistingNosHtml = alreadyExistingArticleNos.join('<br>');
      const alert = await this.alertHelper.show('', 'complexArticleBlueprint.message.articleNosAlreadyExisting', {
        translationParams: [alreadyExistingNosHtml],
        escapeTranslationParams: false,
      });
      if(alert != AlertButton.OK) {
        articleNumberSchemas = [];
      }
    }

    return articleNumberSchemas;
  }

  /** @returns true on success */
  public async runDefinition(definition: ComplexArticleBlueprintDTO, articleNumberSchema: ParsedArticleNumberSchema, runMode: ComplexArticleBlueprintRunMode): Promise<boolean> {
    await this.createProjectService.startNewProjectWithoutAlert();
    await this.createProjectService.createFreeMounting();
    for (const rule of definition.rules) {
      const ruleSucceeded = await this.executeRule(rule.name, rule.params, definition, articleNumberSchema);
      if(!ruleSucceeded) {
        return false;
      }
      this.selectionModelService.removeAllSelections();
    }
    this.selectionModelService.selectAllArticles();
    try {
      await this.saveProject(definition, articleNumberSchema, runMode);
    } catch {
      // already handled in saveProject
      return false;
    }

    // clear the scene
    await this.createProjectService.startNewProject();
    return true;
  }

  private async saveProject(blueprint: ComplexArticleBlueprintDTO, articleNumberSchema: ParsedArticleNumberSchema, runMode: ComplexArticleBlueprintRunMode) {
    const properties = new Properties([]);
    properties.setInt(ArticleBehaviorPropertyDef.COMPOSITE_BASEARTICLE_TYPE_ID, blueprint.articleTypeId);
    properties.setBoolean(ArticleBehaviorPropertyDef.IS_COMPOSITE_PART_SELECTION_ALLOWED, blueprint.flags.isIndividualPartsSelectable);
    properties.setBoolean(ArticleBehaviorPropertyDef.IS_COMPOSITE_PART_MODIFICATION_ALLOWED, blueprint.flags.isIndividualPartsModifiable);
    properties.setBoolean(ArticleBehaviorPropertyDef.IS_COMPOSITE_ARTICLE_STICK_TOGETHER, blueprint.flags.isIndividualPartsStickTogether);
    properties.setBoolean(ArticleBehaviorPropertyDef.IS_INITIAL_ROTATION_IN_MCS_GDS_REQUIRED, blueprint.flags.isInitialRotationRequired);
    properties.setBoolean(ArticleBehaviorPropertyDef.IS_INTERNAL, runMode.internal);
    properties.setBoolean(ArticleBehaviorPropertyDef.NOT_SEARCHABLE, runMode.notSearchable);
    properties.setString(ArticleBehaviorPropertyDef.VERSION_OVERWRITE_MODE, runMode.versionOverwriteMode);

    const baseProjectGroups = await firstValueFrom(this.articleProjectGroupDataService.loadBaseArticleProjectGroups());

    const projectGroup = baseProjectGroups.find((projectGroup) => projectGroup.id == blueprint.projectGroupId);

    if (!projectGroup) {
      this.alertHelper.showError(`folder ${blueprint.projectGroupId} doesn't exist`);
      return;
    }

    const compositeArticleConfiguration = new CompositeConfigurationByGivingAreasVO();
    compositeArticleConfiguration.behaviorProperties = properties.vos;
    compositeArticleConfiguration.compositeArticleNo = articleNumberSchema.articleNo || undefined;
    compositeArticleConfiguration.compositeArticleNo = articleNumberSchema.articleNo || undefined;

    let configName = blueprint.saveMode == SaveAsCompositeMode.BUILDING_BLOCK
      ? articleNumberSchema.buildingBlockName ?? ''
      : ''; // article projects name is assigned to the generated articleId in the backend

    await this.saveProjectService.save(
      configName,
      projectGroup,
      projectGroup.projects,
      blueprint.saveMode,
      false,
      0,
      compositeArticleConfiguration,
      false
    )
  }

  private async executeRule(
    ruleName: string,
    paramValues: PropertyVO[],
    usage: ComplexArticleBlueprintDTO,
    parsedArticleNumberSchema: ParsedArticleNumberSchema,
  ): Promise<boolean> {
    const rule = this.complexArticleBlueprintRuleFactory.findRule(ruleName);
    if (Log.assertExists(rule, `rule with name ${ruleName}`)) {
      try {
        const errors = await rule.execute(paramValues, usage, parsedArticleNumberSchema);
        if (errors.length > 0) {
          this.alertHelper.showError(
            `complexArticleBlueprint.validation.error.in_rule`,
            errors.map((error) => error.message).join('\n'), {translationParams: [ruleName]}
          );
          return false;
        }
      } catch (error) {
        if (error instanceof Error && error.message) {
          Log.error(`Unexpected Error in rule ${ruleName}`, error);
          this.alertHelper.showError(`Unexpected Error in rule ${ruleName}`, ErrorUtils.getErrorInfo(error).message);
        }
        return false;
      }
    }
    return true;
  }

  private parseArticleNumberSchemas(schema: ArticleNumberSchema, buildingBlockName?: string): ParsedArticleNumberSchema[] {
    const results: ParsedArticleNumberSchema[] = [];

    const dynamicNumberLength = this.getDynamicNumberLength(schema);

    if (dynamicNumberLength > 0) {
      const numbers =  ArrayUtils.range(schema.from, schema.to)

      numbers.push(...schema.additional)
      ArrayUtils.removeFromArray(numbers, (number) => schema.excluded.includes(number));

      const dynamicNumbers = numbers
        .map((number) => (
          {
            articleNo: this.replaceXxWIthNumber(schema.articleNumber, number, dynamicNumberLength),
            articlesAmount: number,
            buildingBlockName: buildingBlockName ? this.replaceXxWIthNumber(buildingBlockName, number, dynamicNumberLength) : undefined,
          }));

      results.push(...dynamicNumbers)
    } else {
      results.push({
        articleNo: schema.articleNumber,
        articlesAmount: Number(schema.articleNumber.slice(-2)),
        buildingBlockName,
        // This just takes the last two chars as the amount. Might need to be revisited.
      })
    }
    return results;
  }

  private getDynamicNumberLength(schema: ArticleNumberSchema): number {
    const match = /[xX]+/.exec(schema.articleNumber);
    return match ? match[0].length : 0;
  }

  private replaceXxWIthNumber(dynamicArticleNo: string, number: number, dynamicNumberLength: number): string {
    return dynamicArticleNo.replace(/[xX]+/, String(number).padStart(dynamicNumberLength, '0'));
  }

  public validateBlueprint(usage: ComplexArticleBlueprintDTO): ComplexArticleBlueprintValidation {
    let errorsText: string = '';
    errorsText += this.validateArticleNumberSchema(usage.articleNumberSchema);
    errorsText += usage.projectGroupId && usage.projectGroupId !== -1 ? '' : 'project group not set\n';
    errorsText += usage?.saveMode === SaveAsCompositeMode.NONE ? 'SaveAsComposite mode not set\n' : '';
    errorsText += usage?.articleTypeId === -1 ? 'ArticleType not set\n' : '';

    for (const ruleUsage of usage.rules) {
      const rule =this.complexArticleBlueprintRuleFactory.findRule(ruleUsage.name);

      if (!rule) {
        errorsText += `Rule with name ${ruleUsage.name} not found\n\n`;
        continue;
      }

      const ruleErrors = rule.validateRuleParams(ruleUsage.params);

      if (ruleErrors.length > 0) {
        const ruleName = this.localeResourceManager.getValueByKey('complexArticleBlueprint.rule.title.' + ruleUsage.name);
        errorsText += `Errors in rule ${ruleName}:\n${ruleErrors.join('\n')}\n\n`;
      }
    }

    return errorsText ?
      { errorsText, valid: false }
    : { errorsText: undefined, valid: true };
  }

  private validateArticleNumberSchema(articleNumberSchema: ArticleNumberSchema): string {
    let errorsText = articleNumberSchema.articleNumber.length > 0 ? '' : 'no articlenumberschema set\n';
    const isDynamicSchema = articleNumberSchema?.articleNumber.includes('x');

    if(!isDynamicSchema) {
      return errorsText;
    }

    errorsText += isDynamicSchema && articleNumberSchema?.from > 0 ? '' : '"from" not set to a positive number\n';
    errorsText += isDynamicSchema && articleNumberSchema?.to > 0 ? '' : '"to" not set to a positive number\n';
    errorsText += isDynamicSchema && articleNumberSchema?.to >= articleNumberSchema?.from ? '' : '"to" must be equal or larger than "from"\n';

    const possibleNumLength = this.getDynamicNumberLength(articleNumberSchema);

    if(articleNumberSchema.from.toString().length > possibleNumLength) {
      errorsText += '"from" has more digits than the dynamic part of the article number allows\n';
    }

    if(articleNumberSchema.to.toString().length > possibleNumLength) {
      errorsText += '"to" has more digits than the dynamic part of the article number allows\n';
    }
    return errorsText;
  }
}
